import useWindowSize from "@dana/Common/Hooks/useWindowSize";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import PageBreadcrumbs, { PageBreadcrumbsStyles } from "@insite/content-library/Components/PageBreadcrumbs";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session?.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session?.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isAuthenticated: state.context.session.isAuthenticated,
        isSessionCreated: state.context.session?.userRoles,
        pageTitle: getCurrentPage(state).fields.title,
        cart: getCurrentCartState(state).value,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        displayVmiNavigation:
            currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
        showBreadcumbHeader: getCurrentPage(state).fields["hideBreadcrumbs"],
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    subHeaderLinksStyles: InjectableCss;
    mainHeaderContainer: GridContainerProps;
    topHeader: GridContainerProps;
    headerLinkList: GridItemProps;
    headerVMILinkList?: GridItemProps;
    utilityNavigationContainer: GridContainerProps;
    utilityNavigationWrapper: GridContainerProps;
    utilityTopNavigationWrapper?: GridContainerProps;
    utilityTopNavigationContainer?: GridContainerProps;
    extraLinks?: InjectableCss;
    mainHeader: InjectableCss;
    mainHeaderContainerWrapper?: GridItemProps;
    headerLogo: GridItemProps;
    headerUtilityMenu?: GridItemProps;
    headerUtilityLogo?: GridItemProps;
    mainNavigation: GridItemProps;
    headerSearch: GridItemProps;
    headerBrowseProducts?: GridItemProps;
    headerDanaMenu?: GridItemProps;
    headerReorder: GridItemProps;
    headerMyAccount: GridItemProps;
    subHeader: GridContainerProps;
    page?: MobiusPageProps;
    vmiPageHeader?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    checkoutHeader?: InjectableCss;
    breadcrumbSkipHeader?: GridItemProps;
    pageBreadcrumbsStyles?: PageBreadcrumbsStyles;
    breadcrumWrapperContainer?: GridContainerProps;
    headerDanaMenuSticky?: GridContainerProps;
}

export const headerStyles: HeaderStyles = {
    subHeaderLinksStyles: {},

    skipToContent: {
        skipToContent: {
            css: css`
                span {
                    text-decoration: none;
                }
            `,
        },
    },

    mainHeaderContainer: {
        isFullWidth: true,
    },
    topHeader: {},
    headerLinkList: {
        css: css``,
    },
    headerVMILinkList: {
        css: css``,
    },
    utilityTopNavigationContainer: {
        isFullWidth: true,
        gap: 0,
        css: css`
            background: ${themeColors.primary.black};
        `,
    },

    utilityTopNavigationWrapper: {
        gap: 24,
    },

    utilityNavigationContainer: {
        isFullWidth: true,
        gap: 0,
        css: css`
            background: ${themeColors.primary.black};
            padding: 16px 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 16px;
                        `,
                        css`
                            padding: 16px;
                        `,
                        css`
                            padding: 16px;
                        `,
                        css`
                            padding: 16px 40px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    utilityNavigationWrapper: {
        isFullWidth: true,
        gap: 24,

        css: css`
            .hide-on-desktop {
                display: none;
            }
        `,
    },

    mainHeader: {
        css: css`
            &.isSticky {
                .main-header-container + div[class*="Scrim"] {
                    top: var(--header-height);
                }
            }
        `,
    },

    pageBreadcrumbsStyles: {
        breadcrumbs: {
            css: css`
                width: 100%;
                a {
                    text-decoration: none;

                    &:hover,
                    &:focus,
                    &:focus-visible {
                        text-decoration: none;
                        outline-offset: 0px;
                        border-radius: 0px;
                    }

                    span {
                        text-decoration: none;
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                &&&[data-test-selector="pageBreadcrumbs"] > :not(:nth-last-child(-n + 3)) {
                                    display: none;
                                }
                            `,
                            css`
                                &&&[data-test-selector="pageBreadcrumbs"] > :not(:nth-last-child(-n + 3)) {
                                    display: flex;
                                }
                            `,
                            css`
                                &&&[data-test-selector="pageBreadcrumbs"] > :not(:nth-last-child(-n + 3)) {
                                    display: flex;
                                }
                            `,
                            css`
                                &&&[data-test-selector="pageBreadcrumbs"] > :not(:nth-last-child(-n + 3)) {
                                    display: flex;
                                }
                            `,
                            css`
                                &&&[data-test-selector="pageBreadcrumbs"] > :not(:nth-last-child(-n + 3)) {
                                    display: flex;
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    headerLogo: {
        css: css`
            width: auto;
            flex-basis: auto;
            flex-shrink: 0;
            max-width: none;
            box-sizing: border-box;
            margin: 0px;
            -webkit-box-flex: 0;
            flex-grow: 0;
            min-width: 210px;
            flex-shrink: 1;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    headerUtilityLogo: {
        css: css`
            flex-shrink: 0;
            max-width: none;
            width: auto;
            flex-basis: auto;

            img {
                max-width: 141px;
            }
        `,
    },
    mainNavigation: {
        css: css`
            align-items: center;
        `,
    },
    headerDanaMenu: {
        css: css`
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    headerSearch: {
        width: [12, 12, 6, 6, 6],
        css: css`
            align-items: center;
            padding: 0;
            flex-grow: 1;
            flex-shrink: 1;
            flex-basis: auto;
            max-width: none;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-left: 48px !important;
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            margin-left: 48px !important;
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            margin-left: 80px !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headerBrowseProducts: {
        css: css`
            padding: 0;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    headerReorder: {
        css: css``,
    },
    headerMyAccount: {
        css: css``,
    },
    subHeader: {
        isFullWidth: true,
        css: css``,
    },
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            background: ${themeColors.primary.richBlack};
            overflow: visible;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            overflow: visible;
                        `,
                        css`
                            overflow: visible;
                        `,
                    ],
                    "max",
                )}
            .header-search {
                [class*="PopoverStyle"] {
                    &:not(:empty) {
                        > div {
                            overflow-y: auto;
                            height: 100%;
                            max-height: calc(99vh - var(--header-nav-height));
                        }
                    }
                }
            }
            &.stickyHeader {
                .header-search {
                    [class*="PopoverStyle"] {
                        &:not(:empty) {
                            > div {
                                max-height: calc(99vh - var(--header-height));
                            }
                        }
                    }
                }
                .mobile-menu-wrapper {
                    > [class*="Scrim"] {
                        top: var(--header-height);
                        .drawerHamburger {
                            [class*="ContentBody"] {
                                top: var(--header-height);
                            }
                        }
                    }
                }
            }
        `,
    },
    vmiPageHeader: {},
    container: {},

    headerDanaMenuSticky: {
        css: css`
            display: flex;
        `,
    },
};

const styles = headerStyles;
const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isAuthenticated,
    isSessionCreated,
    pageTitle,
    cart,
    cartUrl,
    homePageUrl,
    displayVmiNavigation,
    showBreadcumbHeader,
}: Props) => {
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [height, setHeight] = useState(0);
    const [navigationheight, setNavigationHeight] = useState(0);

    const setAlertCookies = () => {
        setIsAlertOpen(false);
        setCookie("alertCookies", "true", { expires: 365 });
    };

    let isEnablePayBillTrust = false;
    if (isSessionCreated != null && isSessionCreated !== undefined && isSessionCreated.indexOf("Billtrust") > -1) {
        isEnablePayBillTrust = true;
    }
    let isCheckoutPage = false;
    if (pageTitle === "Payment And Review") {
        isCheckoutPage = true;
    }

    const afterHeader = React.createRef<HTMLSpanElement>();

    const isHidden = true;
    const { width } = useWindowSize();

    useEffect(() => {
        const header = document.querySelector(".main-header") as HTMLElement;
        if (header) {
            const height = header.offsetHeight;
            setHeight(height);
            document.documentElement.style.setProperty("--header-height", `${height}px`);
        }
    }, []);

    useEffect(() => {
        const scrollListener = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const header = document.querySelector("header");
            const headerHeight = header ? header.getBoundingClientRect().height : 0;

            setIsScrolled(scrollTop > headerHeight);
        };

        window.addEventListener("scroll", scrollListener);
        return () => {
            window.removeEventListener("scroll", scrollListener);
        };
    }, []);

    useEffect(() => {
        const header = document.querySelector(".header") as HTMLElement | null;
        const breadcrumb = document.querySelector(".header-breadcrumb-container") as HTMLElement | null;

        if (header) {
            const headerHeight = header.offsetHeight;
            const breadcrumbHeight = breadcrumb ? breadcrumb.offsetHeight : 0;
            const adjustedHeight = headerHeight - breadcrumbHeight;

            setNavigationHeight(adjustedHeight);
            document.documentElement.style.setProperty("--header-nav-height", `${adjustedHeight}px`);
        }
    }, []);

    return (
        <>
            {!displayVmiNavigation && (
                <Page
                    as="header"
                    isHeader={true}
                    {...styles.page}
                    id="pageHeader"
                    className={`header ${isScrolled ? "stickyHeader" : ""}`}
                >
                    <>
                        <SkipNav
                            extendedStyles={styles.skipToContent}
                            text={translate("Skip to Content")}
                            destination={afterHeader}
                        />
                        <EagerLoadingLogoProvider value={true}>
                            <GridContainer gap={0} {...styles.mainHeaderContainer}>
                                {isAuthenticated && (
                                    <GridContainer
                                        className="utility-top-navigation"
                                        {...styles.utilityTopNavigationContainer}
                                    >
                                        <GridContainer {...styles.utilityTopNavigationWrapper}>
                                            <StyledWrapper className="utility-top-navigation-container">
                                                <Zone contentId={id} zoneName="TopHeaderVector" />
                                                <Zone contentId={id} zoneName="TopHeaderSupport" />
                                                <Zone contentId={id} zoneName="TopHeaderCustomerNo" />
                                            </StyledWrapper>
                                        </GridContainer>
                                    </GridContainer>
                                )}

                                <GridContainer className="utility-navigation" {...styles.utilityNavigationContainer}>
                                    <GridItem width={12} {...styles.utilityNavigationWrapper}>
                                        {width !== null && width < 992 && (
                                            <GridItem
                                                width={3}
                                                className="header-utility-logo"
                                                {...styles.headerUtilityLogo}
                                            >
                                                <Zone contentId={id} zoneName="DanaMobileLogo" />
                                            </GridItem>
                                        )}
                                        {/* Check Availability */}
                                        <Zone contentId={id} zoneName="HeaderCheckAvailability" />
                                        {/* Lang dropdown */}
                                        <Zone contentId={id} zoneName="HeaderLanguageDropdown" />
                                        {/* Login/Create Account */}
                                        {width !== null && width < 992 && isAuthenticated && (
                                            <Zone contentId={id} zoneName="HeaderNotifications" />
                                        )}
                                        <Zone contentId={id} zoneName="HeaderLogin" />
                                        {/* Extra Links */}
                                        {isAuthenticated && (
                                            <StyledWrapper {...styles.extraLinks}>
                                                <Zone contentId={id} zoneName="ExtraLinks" />
                                            </StyledWrapper>
                                        )}
                                        {/* Extra Links */}
                                        {/* Show cart if use is logged in */}
                                        <Zone contentId={id} zoneName="HeaderCart" />
                                        {width !== null && width < 992 && (
                                            <GridItem className="header-utility-menu" {...styles.headerUtilityMenu}>
                                                <Zone contentId={id} zoneName="HeaderMenu" />
                                            </GridItem>
                                        )}
                                    </GridItem>
                                </GridContainer>
                                {/* )} */}

                                {/* {width > 992 && ( */}
                                <StyledWrapper
                                    className={`main-header ${isScrolled ? "isSticky" : ""}`}
                                    {...styles.mainHeader}
                                >
                                    <GridItem className="main-header-container" {...styles.mainHeaderContainerWrapper}>
                                        <GridItem className="header-logo" {...styles.headerLogo}>
                                            <Zone contentId={id} zoneName="DanaLogo" />
                                        </GridItem>
                                        <GridItem className="header-search" {...styles.headerSearch}>
                                            <Zone contentId={id} zoneName="DanaSearchInput" />
                                        </GridItem>
                                        <GridItem className="header-browse-products" {...styles.headerBrowseProducts}>
                                            <Zone contentId={id} zoneName="DanaBrowseProducts" />
                                        </GridItem>
                                        {width !== null && width >= 992 && (
                                            <GridItem className="header-dana-menu" {...styles.headerDanaMenu}>
                                                <Zone contentId={id} zoneName="HeaderDanaMenu" />
                                            </GridItem>
                                        )}
                                        {isScrolled && width !== null && width < 992 && (
                                            <GridItem className="header-dana-menu" {...styles.headerDanaMenuSticky}>
                                                <Zone contentId={id} zoneName="HeaderDanaMenu" />
                                            </GridItem>
                                        )}
                                    </GridItem>
                                </StyledWrapper>
                                {/* )} */}
                            </GridContainer>
                            <GridContainer
                                gap={0}
                                {...styles.mainHeaderContainer}
                                className="header-breadcrumb-container"
                            >
                                {!showBreadcumbHeader && (
                                    <GridContainer
                                        className="breadcrumb-wrapper-container"
                                        {...styles.breadcrumWrapperContainer}
                                    >
                                        <GridItem
                                            width={12}
                                            className="breadcrumb-header"
                                            {...styles.breadcrumbSkipHeader}
                                        >
                                            <PageBreadcrumbs extendedStyles={styles.pageBreadcrumbsStyles} />
                                        </GridItem>
                                    </GridContainer>
                                )}
                            </GridContainer>
                        </EagerLoadingLogoProvider>
                        <span ref={afterHeader} tabIndex={-1} />
                    </>
                </Page>
            )}
            {displayVmiNavigation && (
                <Page as="header" isHeader={true} {...styles.vmiPageHeader} id="pageHeader">
                    <>
                        <SkipNav
                            extendedStyles={styles.skipToContent}
                            text={translate("Skip to main content")}
                            destination={afterHeader}
                        />
                        <EagerLoadingLogoProvider value={true}>
                            <GridContainer
                                gap={0}
                                {...styles.mainHeaderContainer}
                                className={`${isCheckoutPage ? "hideOnCheckoutPage" : ""}`}
                            >
                                <GridContainer gap={0} className="top-header" {...styles.topHeader}>
                                    <GridItem
                                        width={12}
                                        align="middle"
                                        className="header-link-list"
                                        {...styles.headerVMILinkList}
                                    >
                                        <Zone contentId={id} zoneName="DanaVMIStaticLinks" />
                                    </GridItem>
                                </GridContainer>

                                <GridContainer gap={0} className="vmi-main-header main-header" {...styles.mainHeader}>
                                    <GridContainer gap={0}>
                                        <GridItem width={2} className="header-logo DanaVmiLogo" {...styles.headerLogo}>
                                            <Zone contentId={id} zoneName="DanaVMILogo" />
                                        </GridItem>
                                        <GridItem
                                            width={4}
                                            className="header-main-navigation"
                                            {...styles.mainNavigation}
                                        >
                                            <Zone contentId={id} zoneName="DanaMainNavigation" />
                                        </GridItem>
                                        <GridItem width={4} className="header-search" {...styles.headerSearch}>
                                            <Zone contentId={id} zoneName="DanaSearchInput" />
                                        </GridItem>

                                        <GridItem
                                            width={2}
                                            className={`vmi-header-account header-my-account ${
                                                isAuthenticated ? "hide-on-mobile" : ""
                                            }`}
                                            {...styles.headerMyAccount}
                                        >
                                            <Zone contentId={id} zoneName="DanaMyAccount" />
                                        </GridItem>
                                    </GridContainer>
                                </GridContainer>
                                {/**  Sub header ends here */}
                            </GridContainer>
                        </EagerLoadingLogoProvider>
                        <span ref={afterHeader} tabIndex={-1} />
                    </>
                </Page>
            )}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
